import React, { useEffect, useMemo, useState } from "react";
import { FaEdit, FaInfoCircle, FaRegEdit, FaUserEdit } from "react-icons/fa";
import {
  MdEdit,
  MdEditDocument,
  MdEditNote,
  MdEditSquare,
} from "react-icons/md";
import { FiChevronLeft, FiEdit, FiSave, FiTrash2, FiArrowRight } from "react-icons/fi";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import FooterButton from "../../components/footerButton";
import { Link, Prompt } from "react-router-dom";
import PickDate from "../../components/common/datePicker";
import { Bounce, toast } from "react-toastify";
import "react-toastify/ReactToastify.css";
import WarningModal from "../../components/clearWarningModal";
import ExitWarningModal from "../../components/exitWarningModal";
import AcceptChangesModal from "../../components/acceptChangesModal";
import ClearRowModal from "../../components/clearRowModal";
import Grade from "../../components/common/Grade";
import GetFunctionalChecklist from "../../hooks/getFunctionalChecklist";
import AddContributorScore from "../../hooks/addContributorScore";
import AddFunctionalChecklistScore from "../../hooks/addFunctionalChecklistScore";
import UpdateFunctionalScore from "../../hooks/updateFunctionalScore";
import AmendFunctionalScore from "../../hooks/amendFunctionalScore";
import GetFunctionalChecklistById from "../../hooks/getFunctionalChecklistById";
import moment from "moment";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useAuth } from "../../services/authProvider";
import {
  emptyScoreItem,
  isNotApplicable,
  toScoreValue,
  toScoresPayload,
} from "../../utils/scoreState";
import { scaleResolverFor } from "../../utils/scoringScale";
import {
  toAssessmentDatePayload,
  toAssessmentDateValue,
} from "../../utils/helper";

const Item1 = ({ setIsOpen, isOpen, onConfirm }) => (
  <>
    <button
      className="btn btn_danger px-5"
      onClick={() => setIsOpen(true)}
      data-toggle="modal"
      data-target="#exampleModal"
    >
      <small>CLEAR</small>
    </button>
    <WarningModal onConfirm={onConfirm} setIsOpen={setIsOpen} isOpen={isOpen} />
  </>
);

const Item2 = ({
  props,
  date,
  handleSign,
  handleSaveLater,
  modifiedScores,
  headings,
  questions,
  setShouldBlockNavigation,
  setConfirmedNavigation,
}) => {
  const handleBack = () => {
    // Disable navigation blocking for "BACK TO ENTER SCORES"
    setShouldBlockNavigation(false);
    setConfirmedNavigation(true);

    // If we have modified scores and headings data, pass it back to functional_score
    const stateToPass =
      modifiedScores && headings && headings.length > 0
        ? {
            date,
            scores: modifiedScores,
            fromReview: true,
            headingsData: headings,
            questions: questions,
          }
        : { date };

    setTimeout(() => {
      props.history.push({
        pathname: "/hcp/patient/function_score",
        state: stateToPass,
      });
    }, 0);
  };

  return (
    <div>
      <button onClick={handleBack} className="btn btn_outline_light px-3 mx-3">
        <FiChevronLeft size={20} />
        <small>BACK TO ENTER SCORES</small>
      </button>
      <button
        onClick={handleSaveLater}
        className="btn btn_outline_light px-3 mx-3"
      >
        <small>SAVE FOR LATER</small>
      </button>
      <button onClick={handleSign} className="btn btn_light px-5 pr-2 ml-3">
        <small>SIGN</small>
      </button>
    </div>
  );
};

const ReviewFunctionalEntry = (props) => {
  const [date, setDate] = useState(
    props.location.state ? props.location.state.date : null
  );
  const provider = JSON.parse(localStorage.getItem("user")).organizations[0]
    .careProvider;
  const patientId = localStorage.getItem("patient");
  const [modifiedScores, setModifiedScores] = useState(
    props.location.state ? props.location.state.scores : null
  );
  const outstandingDoc = localStorage.getItem("documentId");
  const { data: functionalEntry, isLoading: isLoadingEntry } =
    GetFunctionalChecklistById(outstandingDoc);

  const {
    data: addResponse,
    mutate,
    isPending: addIsPending,
    error: addError,
    isSuccess: addSuccess,
  } = AddFunctionalChecklistScore();

  const {
    data: updateResponse,
    mutate: updateMutate,
    isPending: updateIsPending,
    error: updateError,
    isSuccess: updateSuccess,
  } = UpdateFunctionalScore();

  const {
    mutate: amendMutate,
    isPending: amendIsPending,
    error: amendError,
    isSuccess: amendSuccess,
  } = AmendFunctionalScore();

  // Amend mode: editing an already-SIGNED batch reached from the functional
  // summaries Edit button. The user must supply a reason (same gated flow as
  // the care plan), and both SIGN and SAVE FOR LATER hit the amend endpoint —
  // only the status differs (SIGNED vs DRAFT).
  const isAmendMode = Boolean(props.location.state?.amend);
  const entryId = props.location.state?.entryId || outstandingDoc;
  const [editReason, setEditReason] = useState("");
  const [editReasonConfirmed, setEditReasonConfirmed] = useState(false);
  const showBody = !isAmendMode || editReasonConfirmed;

  const [currentUuid, setCurrentUuid] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [headings, setHeadings] = useState([]);
  const [actionType, setActionType] = useState(null); // Track whether user clicked SIGN or SAVE_FOR_LATER
  const [questions, setQuestions] = useState(
    props.location.state?.questions
      ? props.location.state.questions.map((q) => ({ ...q, isEdit: false }))
      : []
  );

  // Navigation blocking state
  const [isExitWarningOpen, setIsExitWarningOpen] = useState(false);
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const { logout } = useAuth();

  // Accept changes modal state
  const [isAcceptChangesOpen, setIsAcceptChangesOpen] = useState(false);
  const [pendingAcceptItemId, setPendingAcceptItemId] = useState(null);
  const [pendingAcceptType, setPendingAcceptType] = useState(null); // 'subheading' or 'question'

  // Clear row modal state
  const [isClearRowOpen, setIsClearRowOpen] = useState(false);
  const [pendingClearItemId, setPendingClearItemId] = useState(null);
  const [pendingClearType, setPendingClearType] = useState(null); // 'subheading' or 'question'

  const { data, isLoading, isError } = GetFunctionalChecklist();
  const activeChecklist = useMemo(
    () => data?.data?.find((item) => item.active) ?? data?.data?.[0],
    [data]
  );

  // Set session storage flag when component mounts
  useEffect(() => {
    sessionStorage.setItem("hasUnsavedFunctionalReview", "true");

    // Clear the flag when component unmounts
    return () => {
      sessionStorage.removeItem("hasUnsavedFunctionalReview");
    };
  }, []);

  useEffect(() => {
    if (addSuccess || updateSuccess || amendSuccess) {

      if(actionType === "SAVE_FOR_LATER")
      toast.success(
        <p className="text-center">
          Your unfinished document <br /> will be saved in
          <br /> OUTSTANDING DOCUMENTS.
        </p>,
        {
          position: "top-right",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: "bg_secondary",
          theme: "colored",
          transition: Bounce,
        }
      );

      if(actionType === "SIGN")
        toast.success(
      <p className="text-center">
          All scores and comments<br /> are now saved in
          <br /> VIEW FUNCTIONAL SUMMARIES.
        </p>,{
      position: "top-right",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: "bg_dark_blue",
          theme: "colored",
          transition: Bounce,
      })
      // Navigate based on action type
      const navigationTimeout = setTimeout(() => {
        if (actionType === "SIGN") {
          // Navigate to functional summaries page
          props.history.push({
            pathname: "/hcp/patient/summary_functions",
            state: { preventBrowserBack: true },
          });
        } else if (actionType === "SAVE_FOR_LATER") {
          // Navigate to outstanding documents page
          props.history.push({
            pathname: "/hcp/outstanding_documents",
            state: { preventBrowserBack: true },
          });
        }
      }, 1000); // Wait 2 seconds to allow user to see the toast

      // Cleanup timeout on unmount
      return () => clearTimeout(navigationTimeout);
    }
  }, [addSuccess, updateSuccess, amendSuccess, actionType, props.history]);
  useEffect(() => {
    const scores = props.location.state ? props.location.state.scores : null;

    if (data && scores && !isLoading && !outstandingDoc) {
      const headings = data.data[0].headings;
      const result = mergeSubheadingScores(scores.scores, headings);
      setHeadings(result);
    } else if (outstandingDoc && functionalEntry && !isLoadingEntry) {
      // The API sends scores as strings that are null for both N/A and
      // unanswered items, so read them through the shared helpers.
      const apiHeadings = functionalEntry.data.headings;
      const headings = apiHeadings.map((item) => {
        return {
          id: item.id,
          name: item.headingName,
          scoringScale: item.scoringScale,
          subheadings: item.subheadings.map((subheading) => {
            const saved = subheading.collaboratorScores?.[0];
            return {
              id: subheading.id,
              name: subheading.subheadingName,
              score: toScoreValue(saved?.score, item.scoringScale),
              notApplicable: isNotApplicable(saved),
              comment: saved?.comment || "",
              isEdit: false,
            };
          }),
        };
      });
      const modifiedOutDocScore = apiHeadings
        .map((item) => {
          return item?.subheadings?.map((subheading) => {
            const saved = subheading.collaboratorScores?.[0];
            return {
              subheadingId: subheading.id,
              score: toScoreValue(saved?.score, item.scoringScale),
              notApplicable: isNotApplicable(saved),
              comment: saved?.comment || "",
            };
          });
        })
        .flat();

      setHeadings(headings);
      // The batch carries the assessment date; the copy on a score row is the
      // same value, but the first subheading is not guaranteed to have a row.
      // It comes back zone-less, so it is read as wall-clock — reading it as
      // UTC and converting to local rolled the picker back a day.
      const newDate = toAssessmentDateValue(
        functionalEntry?.data?.assessmentDate ||
          functionalEntry?.data?.headings?.[0]?.subheadings?.[0]
            ?.collaboratorScores?.[0]?.assessmentDate
      );

      setDate(newDate);
      setModifiedScores({
        status: "DRAFT",
        providerId: provider.id,
        scores: modifiedOutDocScore,
        assessmentDate: newDate,
        patientId,
      });

      // Populate questions from the outstanding document's saved answers
      const savedAnswers = functionalEntry.data.questionAnswers;
      if (savedAnswers && savedAnswers.length > 0) {
        setQuestions(
          savedAnswers.map((qa) => ({
            id: qa.questionId,
            question: qa.questionText,
            answer: qa.answer,
            isEdit: false,
          }))
        );
      }
    }
  }, [data, functionalEntry]);

  // Initialize questions from API data when not passed from enter scores page
  useEffect(() => {
    if (!props.location.state?.questions && data?.data[0]?.questions) {
      const apiQuestions = [...data.data[0].questions]
        .sort((a, b) => a.order - b.order)
        .map((q) => ({ id: q.id, question: q.text, answer: "", isEdit: false }));
      setQuestions(apiQuestions);
    }
  }, [data]);

  // Each score row is read and written on its own heading's scale: the flat
  // scores list spans headings that may be scored on different ranges.
  const resolveScale = scaleResolverFor(headings);

  const mergeSubheadingScores = (scores, headings) => {
    const scoreMap = {};
    scores.forEach((score) => {
      scoreMap[score.subheadingId] = {
        score: score.score,
        notApplicable: isNotApplicable(score),
        comment: score.comment,
      };
    });
    return headings.map((heading) => ({
      ...heading,
      subheadings: heading.subheadings.map((subheading) => {
        const match = scoreMap[subheading.id];
        return {
          ...subheading,
          score: match ? toScoreValue(match.score, heading.scoringScale) : null,
          notApplicable: match ? match.notApplicable : false,
          comment: match ? match.comment : "",
          isEdit: false,
        };
      }),
    }));
  };
  const buildQuestionAnswers = () =>
    questions
      .filter((q) => q.answer.trim() !== "")
      .map((q) => ({
        id: q.answerId || "",
        questionId: q.id,
        answer: q.answer,
      }));

  // The assessment date that gets saved. `date` is what the picker writes and
  // what the header shows, so it is the one the user means; modifiedScores only
  // carries the date the entry arrived with, which is stale the moment the
  // picker moves. Every save path goes through here, so editing the date on
  // this page actually changes it — on a draft, on a sign, and on an amend.
  const assessmentDateForSave = () =>
    toAssessmentDatePayload(date) ||
    toAssessmentDatePayload(modifiedScores?.assessmentDate);

  // Build the amend request body for the /scores/byEntryId/{entryId}/amend
  // endpoint. Status is the only thing that differs between SIGN and SAVE.
  const buildAmendBody = (status) => ({
    patientId: modifiedScores.patientId,
    providerId: modifiedScores.providerId,
    assessmentDate: assessmentDateForSave(),
    status,
    editReason,
    scores: toScoresPayload(modifiedScores.scores, resolveScale),
    questionAnswers: buildQuestionAnswers(),
  });

  const handleSaveLater = () => {
    setActionType("SAVE_FOR_LATER"); // Set action type for navigation

    if (!assessmentDateForSave()) {
      toast.error(
        <p className="text-center">Please choose an assessment date.</p>,
        { position: "top-right", theme: "colored", transition: Bounce }
      );
      return;
    }

    if (isAmendMode) {
      if (!editReason.trim()) {
        toast.error(
          <p className="text-center">Please complete the reason for amending.</p>,
          { position: "top-right", theme: "colored", transition: Bounce }
        );
        return;
      }
      amendMutate({ entryId, data: buildAmendBody("DRAFT") });
      return;
    }

    const draftScores = {
      ...modifiedScores,
      assessmentDate: assessmentDateForSave(),
      scores: toScoresPayload(modifiedScores.scores, resolveScale),
      status: "DRAFT",
      questionAnswers: buildQuestionAnswers(),
    };

    if (outstandingDoc) {
      // Update existing functional score
      updateMutate({
        scoreId: outstandingDoc,
        data: draftScores,
      });
    } else {
      // Create new functional score
      mutate(draftScores);
    }
  };
  const handleAction = (subheadingId, isEdit) => {
    const updatedHeadings = headings.map((heading) => ({
      ...heading,
      subheadings: heading.subheadings.map((subheading) => {
        if (subheading.id === subheadingId) {
          return {
            ...subheading,
            isEdit,
          };
        }
        return subheading;
      }),
    }));
    setHeadings(updatedHeadings);
  };

  // Open accept changes modal for subheading
  const handleOpenAcceptModal = (itemId, type) => {
    setPendingAcceptItemId(itemId);
    setPendingAcceptType(type);
    setIsAcceptChangesOpen(true);
  };

  // Confirm accept changes
  const handleConfirmAcceptChanges = () => {
    if (pendingAcceptType === "subheading") {
      handleAction(pendingAcceptItemId, false);
    } else if (pendingAcceptType === "question") {
      handleQuestionAction(pendingAcceptItemId, false);
    }
    setPendingAcceptItemId(null);
    setPendingAcceptType(null);
  };

  // Open clear row modal
  const handleOpenClearRowModal = (itemId, type) => {
    setPendingClearItemId(itemId);
    setPendingClearType(type);
    setIsClearRowOpen(true);
  };

  // Confirm clear row
  const handleConfirmClearRow = () => {
    if (pendingClearType === "subheading") {
      handleEraseSubheading(pendingClearItemId);
    } else if (pendingClearType === "question") {
      // Clear question answer
      handleQuestionAnswerUpdate(pendingClearItemId, "");
      handleQuestionAction(pendingClearItemId, false);
    }
    setPendingClearItemId(null);
    setPendingClearType(null);
  };
  // A score and N/A are mutually exclusive, so picking one always clears the
  // other.
  const handleGradeUpdate = (subheadingId, score, notApplicable = false) => {
    const nextScore = notApplicable ? null : score;
    const updatedHeadings = headings.map((heading) => ({
      ...heading,
      subheadings: heading.subheadings.map((subheading) => {
        if (subheading.id === subheadingId) {
          return {
            ...subheading,
            score: nextScore,
            notApplicable,
          };
        }
        return subheading;
      }),
    }));
    const updateScores = modifiedScores.scores.map((subheading) => {
      if (subheading.subheadingId === subheadingId) {
        return {
          ...subheading,
          score: nextScore,
          notApplicable,
        };
      }
      return subheading;
    });
    setModifiedScores({ ...modifiedScores, scores: updateScores });
    setHeadings(updatedHeadings);
  };

  const handleCommentUpdate = (headingId, subheadingId, comment) => {
    const updatedHeadings = headings.map((heading) => ({
      ...heading,
      subheadings: heading.subheadings.map((subheading) => {
        if (subheading.id === subheadingId) {
          return {
            ...subheading,
            comment,
          };
        }
        return subheading;
      }),
    }));
    const updateScores = modifiedScores.scores.map((subheading) => {
      if (subheading.subheadingId === subheadingId) {
        return {
          ...subheading,
          comment,
        };
      }
      return subheading;
    });
    setModifiedScores({ ...modifiedScores, scores: updateScores });
    setHeadings(updatedHeadings);
  };

  const handleEraseSubheading = (subheadingId) => {
    // Update headings state - erase score and comment
    const updatedHeadings = headings.map((heading) => ({
      ...heading,
      subheadings: heading.subheadings.map((subheading) => {
        if (subheading.id === subheadingId) {
          return {
            ...subheading,
            score: null, // Back to unanswered, so no radio button is checked
            notApplicable: false,
            comment: "",
            isEdit: false, // Disable radio buttons and comment field
          };
        }
        return subheading;
      }),
    }));

    // Update modifiedScores state - erase score and comment
    const updateScores = modifiedScores.scores.map((subheading) =>
      subheading.subheadingId === subheadingId
        ? emptyScoreItem(subheading.subheadingId)
        : subheading
    );

    setModifiedScores({ ...modifiedScores, scores: updateScores });
    setHeadings(updatedHeadings);
  };
  const handleSign = () => {
    setActionType("SIGN"); // Set action type for navigation

    if (!assessmentDateForSave()) {
      toast.error(
        <p className="text-center">Please choose an assessment date.</p>,
        { position: "top-right", theme: "colored", transition: Bounce }
      );
      return;
    }

    if (isAmendMode) {
      if (!editReason.trim()) {
        toast.error(
          <p className="text-center">Please complete the reason for amending.</p>,
          { position: "top-right", theme: "colored", transition: Bounce }
        );
        return;
      }
      amendMutate({ entryId, data: buildAmendBody("SIGNED") });
      return;
    }

    const signedScores = {
      ...modifiedScores,
      assessmentDate: assessmentDateForSave(),
      scores: toScoresPayload(modifiedScores.scores, resolveScale),
      status: "SIGNED",
      questionAnswers: buildQuestionAnswers(),
    };

    if (outstandingDoc) {
      // Update existing functional score
      updateMutate({
        scoreId: outstandingDoc,
        data: signedScores,
      });
    } else {
      // Create new functional score
      mutate(signedScores);
    }
  };

  // Question handlers
  const handleQuestionAction = (questionId, isEdit) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => (q.id === questionId ? { ...q, isEdit } : q))
    );
  };

  const handleQuestionAnswerUpdate = (questionId, answer) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => (q.id === questionId ? { ...q, answer } : q))
    );
  };

  // Handle navigation blocking
  const handleBlockedNavigation = (location) => {
    if (!confirmedNavigation && shouldBlockNavigation) {
      setPendingLocation(location);
      setIsExitWarningOpen(true);
      return false; // Block navigation
    }
    return true; // Allow navigation
  };

  // Handle user choosing to go back (stay on page)
  const handleGoBack = () => {
    setPendingLocation(null);
    setIsExitWarningOpen(false);
  };

  // Handle user confirming exit (clear data and navigate)
  const handleConfirmExit = () => {
    setIsExitWarningOpen(false);

    // Navigate to the pending location
    if (pendingLocation) {
      // Clear the scores in the background as we navigate
      const clearedScores = {
        ...modifiedScores,
        scores: modifiedScores.scores.map((score) =>
          emptyScoreItem(score.subheadingId)
        ),
      };

      // Update state
      setModifiedScores(clearedScores);
      setHeadings([]);

      // Check if this is a logout attempt
      const isLogout = pendingLocation.pathname === "/user/login" ||
                       pendingLocation.pathname.includes("/user");

      // Set flags and navigate in the next tick to avoid double prompt
      setShouldBlockNavigation(false);
      setConfirmedNavigation(true);

      if (isLogout) {
        // For logout, clear auth and navigate
        setTimeout(() => {
          logout();
          props.history.push(pendingLocation.pathname);
        }, 0);
      } else {
        // For regular navigation, just navigate
        setTimeout(() => {
          props.history.push(pendingLocation.pathname);
        }, 0);
      }
    }
  };

  // Handle custom logout with warning
  const handleLogoutWithWarning = () => {
    setPendingLocation({ pathname: "/logout" });
    setIsExitWarningOpen(true);
  };

  // Effect to handle logout after confirmation
  useEffect(() => {
    if (confirmedNavigation && pendingLocation?.pathname === "/logout") {
      logout();
    }
  }, [confirmedNavigation, pendingLocation, logout]);

  // Disable blocking when successfully saved
  useEffect(() => {
    if (addSuccess || updateSuccess || amendSuccess) {
      setShouldBlockNavigation(false);
      setConfirmedNavigation(true);
    }
  }, [addSuccess, updateSuccess, amendSuccess]);

  // Handle browser close/refresh warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (shouldBlockNavigation && !confirmedNavigation) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return ""; // Some browsers show this message
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldBlockNavigation, confirmedNavigation]);

  return (
    <>
      <Prompt
        when={shouldBlockNavigation && !confirmedNavigation}
        message={handleBlockedNavigation}
      />
      <ExitWarningModal
        isOpen={isExitWarningOpen}
        setIsOpen={setIsExitWarningOpen}
        onGoBack={handleGoBack}
        onConfirm={handleConfirmExit}
      />
      <AcceptChangesModal
        isOpen={isAcceptChangesOpen}
        setIsOpen={setIsAcceptChangesOpen}
        onConfirm={handleConfirmAcceptChanges}
      />
      <ClearRowModal
        isOpen={isClearRowOpen}
        setIsOpen={setIsClearRowOpen}
        onConfirm={handleConfirmClearRow}
      />
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="font-weight-lighter mb-0">
                HCP: REVIEW Functional Scores Entry
              </h5>
              <div className="media align-items-center">
                <h6 className="mb-0">
                  <small>Assessment Date: </small>
                </h6>
                <div className="mb-4 ml-2">
                  <div className="icon-container position-relative">
                    <sup>
                      <FaInfoCircle />
                    </sup>

                    <div className="overlay shadow rounded w-236">
                      <div
                        className="my-2"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <p>
                          The Assessment Date is the date that you SAW or
                          ASSESSED the patient. It is not always the date that
                          you enter the data.
                        </p>
                        <p>
                          eg. <br />
                          if you saw the patient on 10 Jan 2022 and you are
                          entering the data on 10 Jan 2022, select 10 Jan 2022
                          as the ASSESSMENT DATE.
                        </p>
                        <p>
                          if you saw the patient on 10 Jan 2022 and you are
                          entering the data on 12 Jan 2022, select 10 Jan 2022
                          as the ASSESSMENT DATE.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="media-body ml-2 w-186">
                  <PickDate date={date} setDate={setDate} review={true} maxDate={new Date()}/>
                </div>
              </div>
            </div>
            <hr />

            {/* Reason for amending — required when editing an already-signed
                batch reached from functional summaries. The scores below stay
                hidden until this is filled in and submitted via the arrow. */}
            {isAmendMode && (
              <div className="mb-3">
                <small className="text-danger">*</small>
                <div className="d-flex align-items-center">
                  <input
                    type="text"
                    className="form-control"
                    style={{ width: "60%" }}
                    placeholder="mandatory - complete reason for amending this entry"
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn p-0 reason-submit-btn ml-2 d-flex align-items-center justify-content-center"
                    title="Submit reason"
                    disabled={!editReason.trim()}
                    onClick={() =>
                      editReason.trim() && setEditReasonConfirmed(true)
                    }
                    style={{
                      width: 30,
                      height: 30,
                      minWidth: 30,
                      borderRadius: 20,
                    }}
                  >
                    <FiArrowRight size={28} color="#fff" strokeWidth={4} />
                  </button>
                </div>
              </div>
            )}

            {showBody && (
            <div className="noScroll h-90">
              <div className="col-md-4 px-0 pt_1">
                <label
                  htmlFor=""
                  className="small-text border w-358 pl-3 h-35 align-content-center mb-0 rounded-5"
                >
                  Functional Checklist Title:
                  <span className="ml-3">{activeChecklist?.name}</span>
                </label>
              </div>
              <div className="noScroll h-90 verticalScroll py_2">
                <Accordion
                  allowZeroExpanded
                  allowMultipleExpanded
                  className="mb-4 py-0"
                >
                  {(isLoading || isLoadingEntry) && (
                    <SkeletonTheme baseColor="#e9ecef" highlightColor="#f8f9fa">
                      {[...Array(2)].map((_, index) => (
                        <div
                          key={`skeleton-accordion-${index}`}
                          className="mb-2"
                        >
                          <div className="chevron" />
                          {/* Accordion Header Skeleton */}
                          <div
                            className="accordion__primary accordion__button"
                            style={{
                              height: 45,
                              display: "flex",
                              alignItems: "center",
                              borderRadius: "4px",
                            }}
                          >
                            <small>
                              <Skeleton width={200} height={14} />
                            </small>
                          </div>

                          {/* Accordion Content Skeleton */}
                          <div style={{ padding: "16px" }}>
                            {/* Scoring Guidelines Header */}
                            <div className="col-md-3 offset-md-6">
                              <div className="text-center">
                                <small>
                                  <Skeleton width={150} height={12} />
                                </small>
                              </div>
                            </div>

                            {/* Functional Items Skeleton */}
                            {[...Array(3)].map((_, itemIndex) => (
                              <div
                                key={`skeleton-item-${index}-${itemIndex}`}
                                className="row align-items-center my-2"
                              >
                                <div className="col-lg-4 m-lg-0 m-2">
                                  <div
                                    className={`d-flex justify-content-between ${
                                      itemIndex % 2 === 0 ? `bg_light` : ""
                                    } p-3`}
                                  >
                                    <small>
                                      <Skeleton width={120} height={12} />
                                    </small>
                                    <div />
                                    <small>
                                      <FaInfoCircle size={9} />
                                    </small>
                                  </div>
                                </div>
                                <div className="col-lg-8 col-md-8 col-sm-8">
                                  <div className="row align-items-center pr-3 px-md-2 pr-3 px-md-2 d-flex flex-wrap mx-1">
                                    {/* Score Buttons Skeleton */}
                                    <fieldset className="row col-md justify-content-sm-start justify-content-between">
                                      {[...Array(5)].map((_, scoreIndex) => (
                                        <div
                                          key={scoreIndex}
                                          className="col-md-2 text-center"
                                        >
                                          <Skeleton
                                            circle
                                            width={40}
                                            height={40}
                                          />
                                          <div className="mt-1">
                                            <Skeleton width={20} height={10} />
                                          </div>
                                        </div>
                                      ))}
                                    </fieldset>

                                    {/* Comment Input Skeleton */}
                                    <div className="func-score-comment-input text-center px-0 py-0 f-10 outline-none rounded-pill ol-xl-3 col-lg-3 col-md-4">
                                      <Skeleton
                                        width="100%"
                                        height={24}
                                        style={{ borderRadius: "12px" }}
                                      />
                                    </div>

                                    {/* Edit Button Skeleton */}
                                    <div className="col-md-1 d-flex align-items-center mx-0 py_1 col-md justify-content-center my-md-1">
                                      <Skeleton width={20} height={20} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}

                            {/* Next Button Skeleton */}
                            {index < 1 && (
                              <div className="d-flex justify-content-end mt-4 mb-3">
                                <Skeleton
                                  width={99}
                                  height={36}
                                  style={{ borderRadius: "4px" }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </SkeletonTheme>
                  )}
                  {headings &&
                    headings.map((heading, i) => (
                      <div key={i}>
                        <div className="chevron" />
                        <AccordionItem
                          className="mb-2"
                          uuid={i}
                          dangerouslySetExpanded={
                            currentUuid === i ? true : false
                          }
                        >
                          <AccordionItemHeading
                            className="accordion__primary"
                            onClick={() => {
                              setCurrentUuid(i === currentUuid ? null : i);
                            }}
                          >
                            <AccordionItemButton
                              style={{
                                height: 45,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <small>{heading.name}</small>
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel>
                            <div className="col-md-3 offset-md-6">
                              <div className="text-center">
                                &nbsp;&nbsp;
                                <small>
                                  {" "}
                                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Scoring
                                  Guidelines{" "}
                                  <sup>
                                    <FaInfoCircle />
                                  </sup>
                                </small>
                              </div>
                            </div>
                            {heading.subheadings.map((item, key) => (
                              <div
                                key={key}
                                className="row align-items-center my-2"
                              >
                                <div className="col-lg-4 m-lg-0 m-2">
                                  <div
                                    className={`d-flex justify-content-between ${
                                      key % 2 == 0 ? `bg_light` : ""
                                    } p-3`}
                                  >
                                    <small>{item.name}</small>
                                    <div />
                                    <small className="d-none">
                                      <FaInfoCircle size={9} />
                                    </small>
                                  </div>
                                </div>
                                <div className="col-lg-8 col-md-8 col-sm-8">
                                  <div className="row align-items-center pr-3 px-md-2 pr-3 px-md-2 d-flex flex-wrap mx-1">
                                    <fieldset
                                      className="row col-md justify-content-sm-start justify-content-between"
                                      id={"group" + key + i}
                                    >
                                      <Grade
                                        key={`grade-${item.id}-${
                                          item.notApplicable
                                            ? "na"
                                            : item.score ?? "null"
                                        }-${item.isEdit}`}
                                        item={item.id}
                                        scale={heading.scoringScale}
                                        handleGrade={handleGradeUpdate}
                                        isEdit={item.isEdit}
                                        score={item.score}
                                        notApplicable={item.notApplicable === true}
                                        group={"group" + key + i}
                                      />
                                    </fieldset>

                                    <input
                                      type="text"
                                      placeholder="Comments"
                                      value={item.comment}
                                      onChange={(e) =>
                                        handleCommentUpdate(
                                          heading.id,
                                          item.id,
                                          e.target.value
                                        )
                                      }
                                      className="func-score-comment-input text-center px-0 py-0 f-10 outline-none rounded-pill ol-xl-3 col-lg-3 col-md-4"
                                      disabled={!item.isEdit}
                                    />
                                    <div className="col-md-1 d-flex align-items-center mx-0 py_1 col-md justify-content-center my-md-1">
                                      <small
                                        className={`cursor-pointer ${
                                          !item.isEdit ? "d-block" : "d-none"
                                        }`}
                                        onClick={() =>
                                          handleAction(item.id, true)
                                        }
                                      >
                                        <FiEdit
                                          className="text_primary"
                                          size={20}
                                        />
                                      </small>
                                      <small
                                        className={`cursor-pointer ${
                                          item.isEdit ? "d-block" : "d-none"
                                        }`}
                                        title="Accept all changes"
                                        onClick={() =>
                                          handleOpenAcceptModal(item.id, "subheading")
                                        }
                                      >
                                        <FiSave
                                          className="text_primary"
                                          size={25}
                                        />
                                      </small>
                                      <small
                                        className={`cursor-pointer ${
                                          item.isEdit ? "d-block" : "d-none"
                                        }`}
                                        title="Erase all scores and comments"
                                        onClick={() =>
                                          handleOpenClearRowModal(item.id, "subheading")
                                        }
                                      >
                                        <FiTrash2
                                          className="text_danger"
                                          size={25}
                                        />
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {headings.length > i + 1 && (
                              <div className="d-flex justify-content-end mt-4 mb-3">
                                <button
                                  className="btn btn-outline-primary w-99 mr-3 rounded "
                                  onClick={() => {
                                    setCurrentUuid(currentUuid + 1);
                                  }}
                                >
                                  <small>Next</small>
                                </button>
                              </div>
                            )}
                          </AccordionItemPanel>
                        </AccordionItem>
                      </div>
                    ))}

                  {/* Questions Accordion Item */}
                  {questions.length > 0 && <div>
                    <div className="chevron" />
                    <AccordionItem
                      className="mb-2"
                      uuid="questions"
                      dangerouslySetExpanded={
                        currentUuid === "questions" ? true : false
                      }
                    >
                      <AccordionItemHeading
                        className="accordion__primary"
                        onClick={() => {
                          setCurrentUuid(
                            currentUuid === "questions" ? null : "questions"
                          );
                        }}
                      >
                        <AccordionItemButton
                          style={{
                            height: 45,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <small>Questions</small>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel>
                        {questions.map((question, key) => (
                          <div key={question.id} className="mb-3">
                            {/* Question Row */}
                            <div className="row align-items-center">
                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center p-3">
                                  <small className="font-weight-bold">
                                    {question.question}
                                  </small>
                                  <div className="d-flex align-items-center">
                                    <small
                                      className={`cursor-pointer ${
                                        !question.isEdit ? "d-block" : "d-none"
                                      }`}
                                      onClick={() =>
                                        handleQuestionAction(question.id, true)
                                      }
                                    >
                                      <FiEdit
                                        className="text_primary"
                                        size={20}
                                      />
                                    </small>
                                    <small
                                      className={`cursor-pointer mr-2 ${
                                        question.isEdit ? "d-block" : "d-none"
                                      }`}
                                      onClick={() =>
                                        handleOpenAcceptModal(question.id, "question")
                                      }
                                    >
                                      <FiSave
                                        className="text_primary"
                                        size={25}
                                      />
                                    </small>
                                    <small
                                      className={`cursor-pointer ${
                                        question.isEdit ? "d-block" : "d-none"
                                      }`}
                                      onClick={() =>
                                        handleOpenClearRowModal(question.id, "question")
                                      }
                                    >
                                      <FiTrash2
                                        className="text_danger"
                                        size={25}
                                      />
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {/* Textarea Row */}
                            <div className="row mt-2">
                              <div className="col-12">
                                <div className="px-3">
                                  <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="Enter your answer here..."
                                    value={question.answer}
                                    onChange={(e) =>
                                      handleQuestionAnswerUpdate(
                                        question.id,
                                        e.target.value
                                      )
                                    }
                                    disabled={!question.isEdit}
                                    style={{
                                      fontSize: "13px",
                                      border: "1px solid #D1D5DB",
                                      borderRadius: "6px",
                                      backgroundColor: question.isEdit
                                        ? "#fff"
                                        : "#F9FAFB",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </AccordionItemPanel>
                    </AccordionItem>
                  </div>}
                </Accordion>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
      <FooterButton
        child1={<Item1 onConfirm={() => {

    const stateToPass =
      modifiedScores && headings && headings.length > 0
        ? {
            date,
            scores: {
             assessmentDate: assessmentDateForSave(),
             patientId: modifiedScores.patientId,
             providerId: modifiedScores.providerId,
             status: modifiedScores.status,
             scores: modifiedScores.scores.map((score) =>
               emptyScoreItem(score.subheadingId)
             ),
            },
            fromReview: true,
            headingsData: headings,
          }
        : { date };

    // Disable navigation blocking before navigating
    setShouldBlockNavigation(false);
    setConfirmedNavigation(true);

    setTimeout(() => {
      props.history.push({
        pathname: "/hcp/patient/function_score",
        state: stateToPass,
      });
    }, 0);
  }} setIsOpen={setIsOpen} isOpen={isOpen} />}
        child2={
          <Item2
            props={props}
            date={date}
            handleSign={handleSign}
            handleSaveLater={handleSaveLater}
            modifiedScores={modifiedScores}
            headings={headings}
            questions={questions}
            setShouldBlockNavigation={setShouldBlockNavigation}
            setConfirmedNavigation={setConfirmedNavigation}
          />
        }
      />
    </>
  );
};

export default ReviewFunctionalEntry;
